/**
 * I passi del tutorial guidato dell'elenco Prodotti.
 *
 * E' la pagina piu' ricca del BO: tre menu in alto, sei filtri e un menu di riga
 * con nove voci. Ogni passo apre quello che gli serve e `stopItemsListTour`
 * rimette tutto com'era.
 *
 * Le voci del menu di riga aprono ognuna una scheda diversa, e ciascuna ha - o
 * avra' - un tutorial suo: qui si dice solo a cosa serve, non come si usa.
 *
 * I bersagli della pagina sono scoped; le voci dei menu no, perche' Vuetify
 * stacca il contenuto dei menu e lo appende a `[data-app]`, fuori dalla pagina.
 */
const at = name => `[data-tour-scope="items-list"] [data-tour="${name}"]`;

const EXPORT_CSV = '[data-tour="export-csv"]';
const ADD_ITEM = '[data-tour="add-item"]';
const ROW_EDIT = '[data-tour="row-edit"]';

function isVisible(selector) {
	const element = document.querySelector(selector);

	if (!element || element.getClientRects().length === 0) {
		return false;
	}

	/*
	 * Un menu che si sta chiudendo resta a schermo per tutta l'animazione, ma
	 * Vuetify gli toglie subito `menuable__content__active`. Senza questo
	 * controllo un menu in chiusura sembrerebbe ancora aperto e il click di
	 * pulizia, invece di chiuderlo, lo riaprirebbe.
	 */
	const content = element.closest('.v-menu__content');

	return !content || content.classList.contains('menuable__content__active');
}

/**
 * Lascia scattare la chiusura che Vuetify ha gia' in coda.
 *
 * I menu si chiudono al primo click fuori, e il click su "Avanti" e' fuori: la
 * direttiva l'ha registrato in cattura, prima ancora che il pulsante facesse il
 * suo lavoro. Riaprendo subito, il menu si richiuderebbe un istante dopo.
 */
function afterPendingClose() {
	return new Promise(resolve => setTimeout(resolve, 0));
}

/**
 * Aspetta che il bersaglio compaia o sparisca.
 *
 * L'attesa sta qui e non nel `timeout` del passo: cosi' si aspetta solo quando
 * c'e' davvero qualcosa da aprire, e a elenco vuoto il passo si aggancia subito
 * in basso invece di far aspettare a vuoto.
 */
async function waitFor(probe, wanted, timeout = 1500) {
	const deadline = performance.now() + timeout;

	while (performance.now() < deadline) {
		if (isVisible(probe) === wanted) {
			return;
		}

		await new Promise(resolve => setTimeout(resolve, 50));
	}
}

function click(selector) {
	const element = document.querySelector(selector);

	if (element) {
		element.click();
	}
}

async function openMenu(activator, probe) {
	await afterPendingClose();

	if (isVisible(probe)) {
		return;
	}

	const element = document.querySelector(activator);

	if (!element) {
		return;
	}

	element.click();
	await waitFor(probe, true);
}

async function closeMenus() {
	await afterPendingClose();

	// L'attivatore fa da interruttore: il secondo click richiude.
	for (const [activator, probe] of [
		[at('export'), EXPORT_CSV],
		[at('add'), ADD_ITEM],
		[at('row-menu'), ROW_EDIT],
	]) {
		if (isVisible(probe)) {
			click(activator);
			await waitFor(probe, false);
		}
	}
}

const openExportMenu = () => openMenu(at('export'), EXPORT_CSV);
const openAddMenu = () => openMenu(at('add'), ADD_ITEM);
const openRowMenu = () => openMenu(at('row-menu'), ROW_EDIT);


/** Rimette la pagina com'era: menu chiusi. */
export async function stopItemsListTour() {
	await closeMenus();
}

export function createItemsListTourSteps() {
	return [
		{
			body: "Questo e' il catalogo, ed e' il centro di tutto il gestionale: da qui i prodotti vanno sul sito e sui marketplace, e qui tornano le giacenze quando registri un acquisto. Una cosa da mettere subito a fuoco per non confondersi: una riga non e' un prodotto, e' una variante. Un articolo con cinque taglie occupa cinque righe, che condividono nome, marca e categorie ma hanno sku, giacenza e prezzo propri.",
			key: 'intro',
			placement: 'bottom',
			target: at('tutorial'),
			title: 'A cosa serve questa pagina',
		},
		{
			body: "Il valore di magazzino qui in alto non e' un totale fisso: si ricalcola su quello che stai guardando. Se metti un filtro - una marca, una categoria, un fornitore - il numero diventa il valore di quella fetta di magazzino. E' il modo piu' rapido per rispondere a 'quanto ho fermo in un certo reparto'.",
			before: closeMenus,
			key: 'warehouse',
			placement: 'bottom',
			target: at('warehouse'),
			title: 'Il valore di magazzino',
		},
		{
			body: "La ricerca qui e' piu' larga che altrove e parte da tre caratteri. Guarda insieme nome, sku, ean, il codice che usa il fornitore, gli sku dei kit e perfino i codici delle inserzioni sui marketplace. In pratica: se hai in mano un codice qualunque trovato da qualche parte, provalo qui.",
			key: 'search',
			placement: 'bottom',
			target: at('search'),
			title: 'Cercare un prodotto',
		},
		{
			body: "Sei filtri, e si sommano fra loro. Marca, Categorie, Fornitore e Tags accettano piu' valori insieme; Immagini presenti e' la scorciatoia per trovare i prodotti senza foto, che sul sito fanno la figura peggiore. Ricorda che il valore di magazzino la' sopra segue questi filtri.",
			key: 'filters',
			placement: 'bottom',
			target: at('filters'),
			title: 'I filtri',
		},
		{
			body: "Le colonne raccontano la variante da sinistra a destra: com'e' fatta - la colonna Prodotto raccoglie uno sotto l'altro nome, marca, modello, sku, ean, fornitore e categorie, accanto la colonna Variante con gli attributi e la giacenza nella sua unita' - poi la colonna Prezzo: in alto vendita e prezzo con IVA, sotto la linea tratteggiata il costo d'acquisto per unita' e quante unita' compongono un pezzo venduto, in fondo il peso. In fondo ci sono le pastiglie dei marketplace: dicono su quali canali quella variante e' pubblicata e confermata.",
			key: 'table',
			placement: 'bottom',
			/*
			 * L'intestazione, non la tabella intera: con quindici righe la tabella e'
			 * alta quattro schermate, il ritaglio coprirebbe tutto il visibile e il
			 * velo scuro sembrerebbe sparito. L'intestazione poi e' proprio quello di
			 * cui parla il passo.
			 */
			target: `${at('table')} thead`,
			timeout: 250,
			title: 'Come si legge una riga',
		},
		{
			body: "Qui gli interruttori sono due e non vanno confusi. Item spegne l'intero prodotto: sparisce dal sito con tutte le sue varianti. Variant spegne solo quella variante - la taglia esaurita, il colore fuori produzione - e lascia le altre in vendita. Tutti e due partono subito, senza salvataggio da confermare.",
			key: 'status',
			placement: 'right',
			target: at('status'),
			timeout: 250,
			title: 'I due interruttori',
		},
		{
			body: "Una bandierina per ogni lingua del negozio: a colori dove il nome del prodotto e' scritto, in grigio dove manca. Le traduzioni si completano dalla pagina del prodotto, con Modifica dai tre pallini.",
			key: 'langs',
			placement: 'left',
			target: at('langs'),
			timeout: 250,
			title: 'Le lingue del prodotto',
		},
		{
			body: "La freccia in giu' scarica il catalogo in tre modi: CSV, Excel, oppure 'Esporta unica variante in Excel', che dei prodotti con varianti tiene una riga sola invece di tutte - utile per un listino da leggere, meno per lavorarci sopra. L'esportazione rispetta i filtri attivi, e il file viene preparato in coda: arriva con una mail e una notifica.",
			before: openExportMenu,
			key: 'export-csv',
			placement: 'left',
			target: EXPORT_CSV,
			title: 'Scaricare il catalogo',
		},
		{
			body: "Il piu' apre le vie per far entrare prodotti: Aggiungi prodotto apre la pagina del nuovo prodotto - quella grande, con tutte le sue sezioni - mentre Importa da file esterno serve quando i prodotti da caricare sono tanti e li hai gia' in un file. Se hai Shopify collegato, qui compaiono anche le voci per tirarne giu' uno singolo o l'intero catalogo.",
			before: openAddMenu,
			key: 'add-item',
			placement: 'left',
			target: ADD_ITEM,
			title: 'Aggiungere prodotti',
		},
		{
			body: 'I tre pallini a inizio riga sono la cassetta degli attrezzi del singolo prodotto: nove voci, che aprono schede diverse. Le vediamo una per una - qui si dice a cosa servono, ognuna poi ha il suo tutorial dentro.',
			before: openRowMenu,
			key: 'row-menu-intro',
			placement: 'right',
			target: ROW_EDIT,
			timeout: 250,
			title: 'Il menu del prodotto',
		},
		{
			body: "Modifica apre la pagina completa del prodotto: nomi e testi per lingua, categorie, marca, varianti, prezzi, immagini, e le sezioni per pubblicarlo su Amazon ed eBay. E' la scheda piu' lunga del gestionale ed e' li' che si fa il grosso del lavoro.",
			before: openRowMenu,
			key: 'row-edit',
			placement: 'right',
			target: ROW_EDIT,
			timeout: 250,
			title: 'Modifica',
		},
		{
			body: "Stampa etichetta non apre una finestra qui dentro: genera l'etichetta di quella variante - sku, ean, nome, marca e i valori della variante - e la apre in una scheda nuova del browser, pronta da mandare alla stampante.",
			before: openRowMenu,
			key: 'row-print',
			placement: 'right',
			target: '[data-tour="row-print"]',
			timeout: 250,
			title: 'Stampa etichetta',
		},
		{
			body: "Duplica crea un prodotto nuovo partendo da questo, con tutto gia' compilato. E' la via rapida per gli articoli che si somigliano: si duplica, si cambia quello che cambia e si salva, invece di ricompilare da zero una scheda lunga.",
			before: openRowMenu,
			key: 'row-duplicate',
			placement: 'right',
			target: '[data-tour="row-duplicate"]',
			timeout: 250,
			title: 'Duplica',
		},
		{
			body: "Elimina cancella il prodotto, chiedendo conferma. Prima di usarla vale la pena chiedersi se non basti spegnerlo con l'interruttore Item: spento sparisce dal sito ma resta nello storico degli acquisti e delle vendite, cancellato no.",
			before: openRowMenu,
			key: 'row-delete',
			placement: 'right',
			target: '[data-tour="row-delete"]',
			timeout: 250,
			title: 'Elimina',
		},
		{
			body: "Unisci serve a rimettere insieme quello che e' finito doppio: due schede create per lo stesso articolo, tipicamente una a mano e una arrivata da un'importazione. Invece di cancellarne una e perderne lo storico, le si fonde.",
			before: openRowMenu,
			key: 'row-join',
			placement: 'right',
			target: '[data-tour="row-join"]',
			timeout: 250,
			title: 'Unisci',
		},
		{
			body: "Acquisti apre lo storico di carico della variante: documenti, fornitore, quantita' e prezzi. E' la risposta veloce a 'quanto l'ho pagata l'ultima volta'.",
			before: openRowMenu,
			key: 'row-buy',
			placement: 'right',
			target: '[data-tour="row-buy"]',
			timeout: 250,
			title: 'Acquisti',
		},
		{
			body: 'Vendite mostra gli ordini che contengono la variante, con canale, cliente, stato, importi e spedizione. Serve per capire quanto e dove viene venduta davvero.',
			before: openRowMenu,
			key: 'row-sell',
			placement: 'right',
			target: '[data-tour="row-sell"]',
			timeout: 250,
			title: 'Vendite',
		},
		{
			body: "Statistiche mette insieme le due cose di prima e ne fa un quadro: andamento nel tempo, margini, giri di magazzino. E' quello che si guarda prima di decidere se riordinare un articolo o smettere di tenerlo.",
			before: openRowMenu,
			key: 'row-stats',
			placement: 'right',
			target: '[data-tour="row-stats"]',
			timeout: 250,
			title: 'Statistiche',
		},
		{
			body: "L'ultima voce compare solo con Shopify collegato: rilegge quel prodotto da Shopify e riporta qui quello che e' cambiato di la'. Serve quando il prodotto viene gestito su Shopify e Azseller deve solo stargli dietro.",
			before: openRowMenu,
			key: 'row-shopify',
			placement: 'right',
			target: '[data-tour="row-shopify"]',
			timeout: 250,
			title: 'Aggiorna da Shopify',
		},
	];
}
